// frontend/src/pages/ForgotPassword.tsx
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildApiUrl, API_CONFIG } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useCooldown } from '../hooks/useCooldown';
import { resolveAfterCredential } from '../utils/returnTo';
import ParticleBackground from '../components/ParticleBackground';
import { WakeLogo } from '../components/brand/WakeLogo';
import { Eye, EyeOff } from 'lucide-react';

// Mirror de ResetPasswordDto.newPassword no backend
// (backend/src/dto/forgot-password.dto.ts). Manter sincronizado.
const PASSWORD_COMPLEXITY_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
const PASSWORD_RULES_HINT =
  'Mín. 8 caracteres com 1 minúscula, 1 maiúscula, 1 número e 1 caractere especial (@$!%*?&)';

const ForgotPassword = () => {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  // Contador local, e local é o único jeito possível aqui: o
  // `/auth/forgot-password` devolve a MESMA string genérica no cooldown e no
  // caminho normal, e NÃO manda `retryInSeconds` — uma resposta distinta viraria
  // oráculo de "esta conta pediu reset agora". Então a tela conta os 60 s por
  // conta própria e o backend silenciosamente não reenvia dentro da janela.
  const [cooldownLeft, setCooldownLeft] = useCooldown();

  /**
   * Pede o código ao backend. Serve ao passo 1 e ao reenvio do passo 2 — é a
   * mesma chamada, e duplicá-la deixaria os dois caminhos divergindo no
   * tratamento de erro e no cooldown.
   */
  const requestCode = async (opts: { reenvio: boolean }) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.FORGOT_PASSWORD), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao solicitar código');
      }

      setSuccess(
        opts.reenvio
          ? 'Código reenviado! Use o mais recente — o anterior deixou de valer.'
          : 'Código enviado! Verifique seu e-mail.',
      );
      // 61 e não 60: a janela do backend conta da EMISSÃO, alguns ms depois
      // desta resposta, então liberar em 60,0s ainda bateria no cooldown.
      setCooldownLeft(61);
      setStep('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao solicitar código');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    await requestCode({ reenvio: false });
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem');
      setLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres');
      setLoading(false);
      return;
    }

    if (!PASSWORD_COMPLEXITY_REGEX.test(newPassword)) {
      setError(PASSWORD_RULES_HINT);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.RESET_PASSWORD), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          code,
          newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // NestJS ValidationPipe retorna message como array; normaliza p/ string.
        const msg = Array.isArray(data.message)
          ? data.message.join(' • ')
          : data.message;
        throw new Error(msg || 'Erro ao redefinir senha');
      }

      // O backend já autentica no reset (mesmo contrato do /auth/login), então
      // não há por que mandar o usuário redigitar a senha que ele acabou de
      // definir. Com MFA ativo/obrigatório ele devolve o desafio em vez do JWT:
      // segue direto para o 2º fator com o ticket, sem pedir a senha de novo.
      if (data.mfa_required) {
        navigate('/mfa-challenge', {
          state: {
            mfa_ticket: data.mfa_ticket,
            methods: data.methods,
            from: resolveAfterCredential(location.state?.from),
          },
        });
        return;
      }
      if (data.mfa_enrollment_required) {
        navigate('/mfa-enrollment', {
          state: {
            enroll_ticket: data.enroll_ticket,
            from: resolveAfterCredential(location.state?.from),
          },
        });
        return;
      }
      if (data.passkey_enrollment_required) {
        navigate('/passkey-enrollment', {
          state: {
            pk_enroll_ticket: data.pk_enroll_ticket,
            from: resolveAfterCredential(location.state?.from),
          },
        });
        return;
      }

      if (data.access_token) {
        setSuccess('Senha redefinida com sucesso! Entrando...');
        await login(data.access_token, data.user);
        navigate(resolveAfterCredential(location.state?.from), { replace: true });
        return;
      }

      // Sem token: é o ramo de e-mail inexistente, que responde 200 com a
      // mensagem genérica para não virar oráculo de enumeração. Mantém o
      // comportamento antigo — nada a fazer além de mandar para o login.
      setSuccess('Senha redefinida com sucesso! Redirecionando...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center overflow-hidden relative bg-canvas">
      <ParticleBackground />

      <div className="relative z-10 w-full max-w-md px-4">
        <div className="bg-surface border border-border-subtle rounded-[5px] p-10 shadow-2xl text-center">
          <div className="mb-5 flex justify-center">
            <WakeLogo tone="light" />
          </div>
          
          <h1 className="text-body text-2xl font-bold mb-8">
            {step === 'email' ? 'Recuperar Senha' : 'Redefinir Senha'}
          </h1>

          {step === 'email' ? (
            <form onSubmit={handleRequestCode} className="space-y-6">
              {error && (
                <div className="bg-[#fbe0e0] dark:bg-red-900/30 border border-red-500 text-[#c0392b] dark:text-red-400 px-4 py-3 rounded-[5px] text-sm">
                  {error}
                </div>
              )}
              {success && (
                <div className="bg-[#e7f8ef] dark:bg-emerald-900/30 border border-green-500 text-[#16a34a] dark:text-emerald-400 px-4 py-3 rounded-[5px] text-sm">
                  {success}
                </div>
              )}

              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-4 pr-12 border border-border-strong rounded-[5px] bg-surface-muted text-body placeholder:text-subtle focus:border-[#2563eb] focus:ring-2 focus:ring-[#bfdbfe] dark:focus:ring-blue-800 focus:ring-opacity-30 transition-all duration-300"
                  placeholder="Digite seu e-mail"
                  autoComplete="email"
                />
                <i className="fas fa-envelope absolute right-4 top-1/2 transform -translate-y-1/2 text-[#2563eb] dark:text-blue-400 text-lg"></i>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#2563eb] hover:bg-[#1d55d6] text-white font-bold py-4 px-6 rounded-[5px] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>Enviando... <i className="fas fa-spinner fa-spin ml-2"></i></>
                ) : (
                  <>Enviar Código <i className="fas fa-paper-plane ml-2"></i></>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-6">
              {error && (
                <div className="bg-[#fbe0e0] dark:bg-red-900/30 border border-red-500 text-[#c0392b] dark:text-red-400 px-4 py-3 rounded-[5px] text-sm">
                  {error}
                </div>
              )}
              {success && (
                <div className="bg-[#e7f8ef] dark:bg-emerald-900/30 border border-green-500 text-[#16a34a] dark:text-emerald-400 px-4 py-3 rounded-[5px] text-sm">
                  {success}
                </div>
              )}

              <div className="relative">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className="w-full px-4 py-4 pr-12 border border-border-strong rounded-[5px] bg-surface-muted text-body placeholder:text-subtle focus:border-[#2563eb] focus:ring-2 focus:ring-[#bfdbfe] dark:focus:ring-blue-800 focus:ring-opacity-30 transition-all duration-300 text-center text-2xl tracking-widest"
                  placeholder="000000"
                />
                <i className="fas fa-key absolute right-4 top-1/2 transform -translate-y-1/2 text-[#2563eb] dark:text-blue-400 text-lg"></i>
              </div>

              <button
                type="button"
                onClick={() => requestCode({ reenvio: true })}
                disabled={loading || cooldownLeft > 0}
                className="text-xs text-blue-400 hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed text-left"
              >
                {cooldownLeft > 0
                  ? `Aguarde ${cooldownLeft}s para reenviar`
                  : 'Não recebeu ou o código expirou? Reenviar código'}
              </button>

              <div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="w-full px-4 py-4 pr-16 border border-border-strong rounded-[5px] bg-surface-muted text-body placeholder:text-subtle focus:border-[#2563eb] focus:ring-2 focus:ring-[#bfdbfe] dark:focus:ring-blue-800 focus:ring-opacity-30 transition-all duration-300"
                    placeholder="Nova senha"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#2563eb] dark:text-blue-400 hover:text-blue-400"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-subtle mt-2 text-left">{PASSWORD_RULES_HINT}</p>
              </div>

              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full px-4 py-4 pr-16 border border-border-strong rounded-[5px] bg-surface-muted text-body placeholder:text-subtle focus:border-[#2563eb] focus:ring-2 focus:ring-[#bfdbfe] dark:focus:ring-blue-800 focus:ring-opacity-30 transition-all duration-300"
                  placeholder="Confirmar senha"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#2563eb] dark:text-blue-400 hover:text-blue-400"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleBackToEmail}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-4 px-6 rounded-[5px] transition-all duration-300"
                >
                  Voltar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-[#2563eb] hover:bg-[#1d55d6] text-white font-bold py-4 px-6 rounded-[5px] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>Redefinindo... <i className="fas fa-spinner fa-spin ml-2"></i></>
                  ) : (
                    <>Redefinir</>
                  )}
                </button>
              </div>
            </form>
          )}

          <div className="mt-6">
            <button
              onClick={() => navigate('/login')}
              className="text-[#2563eb] dark:text-blue-400 hover:text-blue-400 font-bold transition-colors duration-300"
             type="button">
              Voltar para o login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;